import { Check, Star } from "lucide-react";

const tiers = [
  {
    name: "Basic",
    tagline: "Get your operations digital",
    features: [
      "All Phase 1 operations features",
      "Pickup agent app & live dashboard",
      "Automatic WhatsApp & email receipts",
      "Monthly message bundle included",
      "Fair per-message rates beyond your bundle",
      "Daily, weekly & monthly reports",
    ],
  },
  {
    name: "Pro",
    tagline: "Operations + business intelligence",
    popular: true,
    features: [
      "Everything in Basic",
      "All Phase 2 intelligence features",
      "Stock & detergent tracking",
      "Customer location heatmap",
      "A larger monthly message bundle",
      "Deeper reports & analytics",
    ],
  },
  {
    name: "Premium",
    tagline: "The full suite for growing brands",
    features: [
      "Everything in Pro",
      "Multi-branch support",
      "Custom branding (your logo & colours)",
      "Priority support",
      "The largest message bundle",
      "CEO-level business intelligence reports",
    ],
  },
];

export default function Pricing() {
  return (
    <section id="pricing" data-testid="pricing-table-section" className="bg-white py-16 sm:py-24">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <span className="inline-block text-xs font-bold uppercase tracking-widest text-[#1ABC9C] bg-[#E8F8F5] px-4 py-1.5 rounded-full mb-4">Subscription Tiers</span>
        <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#2C3E50] max-w-2xl">
          Three plans. One that's right for you.
        </h2>
        <p className="mt-4 text-lg text-[#5D6D7E] leading-relaxed max-w-2xl">
          Simple annual subscriptions, sized to your business. Pick a plan and we'll prepare a tailored quote — no hidden charges.
        </p>
        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6 items-stretch">
          {tiers.map((t) => (
            <div
              key={t.name}
              data-testid={`pricing-card-${t.name.toLowerCase()}`}
              className={`relative rounded-2xl p-8 flex flex-col border-2 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl ${
                t.popular ? "bg-[#1B4F72] border-[#1B4F72] shadow-lg md:-mt-4 md:mb-[-1rem]" : "bg-white border-[#E2E8F0]"
              }`}
            >
              {t.popular && (
                <span data-testid="pricing-popular-badge" className="absolute -top-4 left-1/2 -translate-x-1/2 inline-flex items-center gap-1.5 bg-[#1ABC9C] text-white text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full">
                  <Star className="w-3.5 h-3.5 fill-white" /> Most Popular
                </span>
              )}
              <h3 className={`font-display text-xl font-bold ${t.popular ? "text-white" : "text-[#2C3E50]"}`}>{t.name}</h3>
              <p className={`mt-1 text-sm ${t.popular ? "text-[#AED6F1]" : "text-[#5D6D7E]"}`}>{t.tagline}</p>
              <p className={`mt-6 font-display text-2xl font-extrabold ${t.popular ? "text-[#1ABC9C]" : "text-[#1B4F72]"}`}>
                Tailored annual quote
              </p>
              <ul className="mt-6 space-y-3 flex-1">
                {t.features.map((f, i) => (
                  <li key={i} className="flex items-start gap-2.5">
                    <Check className="w-5 h-5 shrink-0 mt-0.5 text-[#1ABC9C]" />
                    <span className={`text-base leading-relaxed ${t.popular ? "text-[#EBF5FB]" : "text-[#2C3E50]"}`}>{f}</span>
                  </li>
                ))}
              </ul>
              <a
                href="#contact"
                data-testid={`pricing-cta-${t.name.toLowerCase()}`}
                className={`mt-8 text-center font-bold px-6 py-3.5 rounded-full transition-colors ${
                  t.popular
                    ? "bg-[#1ABC9C] hover:bg-[#16A085] text-white"
                    : "bg-[#EBF5FB] hover:bg-[#AED6F1] text-[#1B4F72]"
                }`}
              >
                Request {t.name} quote
              </a>
            </div>
          ))}
        </div>
        <div data-testid="messaging-fees-card" className="mt-12 bg-[#EBF5FB] rounded-2xl p-8">
          <h3 className="font-display text-lg sm:text-xl font-semibold text-[#1B4F72]">Messaging, sized to your volume</h3>
          <p className="mt-1 text-base text-[#5D6D7E] max-w-3xl">
            Every plan includes a monthly bundle of WhatsApp and SMS messages. If you send more, messaging simply scales with your volume at a fair per-message rate — we'll size it to your business when we prepare your quote.
          </p>
        </div>
      </div>
    </section>
  );
}
